import React from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    IconButton, 
    Button,
    Breakpoint,
    Typography,
    Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export interface CentralizedDialogProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    children: React.ReactNode;
    primaryAction?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
    secondaryAction?: {
        label: string;
        onClick: () => void;
    };
    maxWidth?: Breakpoint;
    icon?: React.ReactNode;
    iconColor?: string;
}

const CentralizedDialog: React.FC<CentralizedDialogProps> = ({
    open,
    onClose,
    title,
    children,
    primaryAction,
    secondaryAction,
    maxWidth = 'sm',
    icon,
    iconColor = '#2e7d32'
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth
            aria-labelledby="dialog-title"
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    paddingBottom: 2,
                    boxShadow: 6,
                    maxHeight: '85vh'
                },
            }}
        >

            {/* TITOLI */}
            <DialogTitle
                id="dialog-title"
                sx={{
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    padding: '24px 24px 16px',
                    textAlign: 'center',
                    color: '#1a237e'
                }}
            >
                {title}

                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        color: (theme) => theme.palette.grey[500],
                        transition: '0.2s',
                        '&:hover': {
                            color: (theme) => theme.palette.grey[800],
                            backgroundColor: 'rgba(0,0,0,0.04)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* CONTENUTO */}
            <DialogContent 
                sx={{ 
                    px: 4, 
                    pb: 3, 
                    pt: 1,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                {icon && (
                    <Box sx={{ mb: 2, mt: 1 }}>
                        {React.cloneElement(icon as React.ReactElement, {
                            sx: { fontSize: 64, color: iconColor }
                        })}
                    </Box>
                )}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        lineHeight: 1.7,
                        color: '#424242',
                        fontSize: '1rem'
                    }}
                >
                    {children}
                </Typography>
            </DialogContent>

            {/* BOTTONI */}
            {(primaryAction || secondaryAction) && (
                <DialogActions sx={{ px: 3 }}>
                    {secondaryAction && (
                        <Button onClick={secondaryAction.onClick} color="inherit">
                            {secondaryAction.label}
                        </Button>
                    )}

                    {primaryAction && (
                        <Button
                            onClick={primaryAction.onClick}
                            disabled={primaryAction.disabled}
                            variant="contained"
                            sx={{
                                borderRadius: '30px',
                                paddingX: 4,
                                paddingY: 1.2,
                                fontWeight: 600,
                                backgroundColor: '#1565c0',
                                textTransform: 'none',
                                fontSize: '1rem',
                                '&:hover': {
                                    backgroundColor: '#0d47a1'
                                }
                            }}
                        >
                            {primaryAction.label}
                        </Button>
                    )}
                </DialogActions>
            )}

        </Dialog>
    );
};

export default CentralizedDialog;
